import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getCompletedSplitsInCurrentBillingCycle, usesWalletBillingForMerchant } from "@/lib/billing";
import { getPreferredMerchantAssociation, normalizeShopDomain } from "@/lib/merchantSelection";
import { fetchSupabaseFunction } from "@/lib/shopifySessionToken";
import { getShopifyEmbeddedContext } from "@/lib/shopifyEmbedded";
import { clearMerchantSupportSession, getMerchantSupportSession } from "@/lib/merchantSupportSession";

const INTRO_TRIAL_SPLIT_LIMIT = 100;

interface SubscriptionInfo {
  billingProvider?: string | null;
  plan: string;
  status: string;
  trialEndsAt: Date | null;
  trialStartedAt: Date | null;
  billingCycleStart: Date | null;
  splitsThisMonth: number;
  daysRemaining: number | null;
  isTrialExpired: boolean;
  isActive: boolean;
  canCreateSplit: boolean;
  splitLimit: number | null;
  freeSplitAllowance: number | null;
  overageFeePercent: number;
}

export function useSubscription() {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const lastSelectionKey = useRef<string | null>(null);
  const embeddedShop = normalizeShopDomain(
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("shop") : null,
  );
  const isEmbeddedShopifyMerchant = getShopifyEmbeddedContext().embedded && embeddedShop;

  useEffect(() => {
    // Don't do anything while auth is still loading
    if (authLoading) {
      setLoading(true);
      return;
    }

    // If no user, reset state and stop loading
    if (!user) {
      setSubscription(null);
      setMerchantId(null);
      setLoading(false);
      lastSelectionKey.current = null;
      return;
    }

    const storedSupportSession = getMerchantSupportSession();
    const selectionKey = `${user.id}:${embeddedShop || "no-shop"}:${localStorage.getItem("active_merchant_id") || "no-stored-merchant"}:${storedSupportSession?.sessionId || "no-support-session"}`;

    if (lastSelectionKey.current === selectionKey) {
      return;
    }

    const fetchSubscription = async () => {
      setLoading(true);
      lastSelectionKey.current = selectionKey;

      try {
        let selectedMerchantId: string | null = null;

        if (storedSupportSession) {
          const { data: activeSupportSession, error: supportError } = await (supabase as any)
            .rpc("get_active_merchant_support_session");

          if (
            !supportError &&
            activeSupportSession?.session_id === storedSupportSession.sessionId &&
            activeSupportSession?.merchant_id === storedSupportSession.merchantId
          ) {
            selectedMerchantId = storedSupportSession.merchantId;
          } else {
            clearMerchantSupportSession();
          }
        }

        // Get merchant associations and choose the active one the same way the dashboard does.
        if (!selectedMerchantId) {
          const { data: merchantUsers, error: muError } = await supabase
            .from('merchant_users')
            .select('merchant_id, merchants!inner(shopify_store_domain)')
            .eq('user_id', user.id);

          if (muError || !merchantUsers || merchantUsers.length === 0) {
            console.log('No merchant association found for subscription');
            setSubscription(null);
            setMerchantId(null);
            setLoading(false);
            return;
          }

          const storedMerchantId = localStorage.getItem("active_merchant_id");
          const merchantUser = getPreferredMerchantAssociation(merchantUsers as any[], {
            preferredShop: embeddedShop,
            storedMerchantId,
          });

          if (!merchantUser) {
            console.log('No preferred merchant association found for subscription');
            setSubscription(null);
            setMerchantId(null);
            setLoading(false);
            return;
          }

          selectedMerchantId = merchantUser.merchant_id;
        }

        setMerchantId(selectedMerchantId);

        // Get merchant details
        const { data: merchant, error: mError } = await supabase
          .from('merchants')
          .select('billing_provider, billing_cycle_start, plan, platform_type, signup_source, splits_this_month, subscription_status, trial_ends_at, trial_started_at')
          .eq('id', selectedMerchantId)
          .single();

        if (mError || !merchant) {
          console.log('No merchant found for subscription');
          setSubscription(null);
          setLoading(false);
          return;
        }

        const usesWalletBilling = usesWalletBillingForMerchant(merchant as any);

        const currentCycleSplits = getCompletedSplitsInCurrentBillingCycle({
          billing_cycle_start: merchant.billing_cycle_start,
          splits_this_month: merchant.splits_this_month,
        });

        if (merchant.platform_type === "shopify" && (!usesWalletBilling || isEmbeddedShopifyMerchant)) {
          try {
            const response = await fetchSupabaseFunction("sync-shopify-billing-state", {
              body: { merchantId: selectedMerchantId },
            });

            if (response.ok) {
              const payload = await response.json();
              if (payload?.subscription) {
                const syncedSubscription = payload.subscription;
                setSubscription({
                  ...syncedSubscription,
                  billingProvider: syncedSubscription.billingProvider || merchant.billing_provider || "shopify",
                  billingCycleStart: syncedSubscription.billingCycleStart ? new Date(syncedSubscription.billingCycleStart) : null,
                  trialEndsAt: syncedSubscription.trialEndsAt ? new Date(syncedSubscription.trialEndsAt) : null,
                  trialStartedAt: syncedSubscription.trialStartedAt ? new Date(syncedSubscription.trialStartedAt) : null,
                });
                setLoading(false);
                return;
              }
            } else {
              console.warn("Failed to sync Shopify billing state", await response.text());
            }
          } catch (syncError) {
            console.warn("Error syncing Shopify billing state:", syncError);
          }
        }

        const now = new Date();
        const trialEndsAt = merchant.trial_ends_at ? new Date(merchant.trial_ends_at) : null;
        const trialStartedAt = merchant.trial_started_at ? new Date(merchant.trial_started_at) : null;
        const billingCycleStart = merchant.billing_cycle_start ? new Date(merchant.billing_cycle_start) : null;

        // Calculate days remaining in trial
        let daysRemaining: number | null = null;
        let isTrialExpired = false;

        if (trialEndsAt) {
          const diff = trialEndsAt.getTime() - now.getTime();
          daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));
          isTrialExpired = daysRemaining < 0;
        }

        const effectivePlan =
          merchant.plan === "trial" && isTrialExpired
            ? "trial_expired"
            : merchant.plan;

        const isActive =
          merchant.subscription_status === "active" ||
          (merchant.subscription_status === "trialing" && !isTrialExpired);

        const freeSplitAllowances: Record<string, number | null> = {
          trial: INTRO_TRIAL_SPLIT_LIMIT,
          free: 5,
          starter: 25,
          growth: 100,
          enterprise: null,
          trial_expired: null,
        };

        const overageFees: Record<string, number> = {
          trial: 0,
          free: 2,
          starter: 1.5,
          growth: 1,
          enterprise: 0,
          trial_expired: 0,
        };

        const splitLimit: number | null = effectivePlan === "trial" ? INTRO_TRIAL_SPLIT_LIMIT : null;
        const canCreateSplit =
          isActive && (effectivePlan !== "trial" || currentCycleSplits < INTRO_TRIAL_SPLIT_LIMIT);

        setSubscription({
          billingProvider: usesWalletBilling ? "razorpay" : merchant.billing_provider,
          plan: effectivePlan,
          status: merchant.subscription_status,
          trialEndsAt,
          trialStartedAt,
          billingCycleStart,
          splitsThisMonth: currentCycleSplits,
          daysRemaining: daysRemaining !== null && daysRemaining > 0 ? daysRemaining : null,
          isTrialExpired,
          isActive,
          canCreateSplit,
          splitLimit,
          freeSplitAllowance: freeSplitAllowances[effectivePlan] ?? null,
          overageFeePercent: overageFees[effectivePlan] ?? 0,
        });
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user, authLoading, embeddedShop, isEmbeddedShopifyMerchant]);

  return { subscription, loading, merchantId };
};
